import type { FunctionComponent } from "react"

import { Divider } from "@jobteaser/spark/components/Divider"
import { ExternalLink } from "@jobteaser/spark/components/Icons/ExternalLink"
import { Text } from "@jobteaser/spark/components/Text"
import IconOrganization from "@jobteaser/ui-kit/assets/icons/jds_organization.svg"
import IconPin from "@jobteaser/ui-kit/assets/icons/jds_pin.svg"
import IconWork from "@jobteaser/ui-kit/assets/icons/jds_work.svg"
import { Tooltip } from "@jobteaser/ui-kit/components/Tooltip/Tooltip"

import { KeyInformation } from "@/modules/companies/client/components/KeyInformation/KeyInformation"
import { BUSINESS_TYPES, BUSINESS_TYPES_I18N } from "@/modules/companies/client/constants/enums"
import { useCompanyExternalLinkTrack } from "@/modules/companies/client/hooks/tracking/useCompanyExternalLinkTrack"
import { useTranslation } from "@/modules/i18n/components/useTranslation"

import styles from "./HeaderKeyInformation.module.css"

type HeaderKeyInformationProps = {
  businessType?: string
  headquarterAddress?: string
  sector?: string
}

export const HeaderKeyInformation: FunctionComponent<HeaderKeyInformationProps> = ({
  businessType,
  headquarterAddress,
  sector,
}) => {
  const { t } = useTranslation(["companies_client", "shared_business_types", "shared_company_categories"])
  const companyExternalLinkTrack = useCompanyExternalLinkTrack()

  return (
    <div className={styles.headerKeyInformation}>
      {!!businessType && businessType !== BUSINESS_TYPES.COMPANY_PAGE_BUSINESS_TYPE_UNSPECIFIED && (
        <>
          <KeyInformation
            className={styles.headerKeyInformation__keyInformation}
            title={t("companies_client.header.companyType")}
            icon={IconOrganization}
            colorReverse
            noSpacing
          >
            <Text variant="body1" weight="semiBold" data-testid="company_header_business_type">
              {t(`shared_business_types.${BUSINESS_TYPES_I18N[businessType]}`)}
            </Text>
          </KeyInformation>
          <Divider orientation="horizontal" onBackground="dark" className={styles.headerKeyInformation__separator} />
        </>
      )}
      {!!sector && (
        <>
          <KeyInformation
            className={styles.headerKeyInformation__keyInformation}
            title={t("companies_client.header.sector")}
            icon={IconWork}
            colorReverse
            noSpacing
          >
            <Text variant="body1" weight="semiBold" data-testid="company_header_sector">
              {t(`shared_company_categories.${sector}`)}
            </Text>
          </KeyInformation>
          {headquarterAddress && (
            <Divider orientation="horizontal" onBackground="dark" className={styles.headerKeyInformation__separator} />
          )}
        </>
      )}
      {!!headquarterAddress && (
        <KeyInformation
          className={styles.headerKeyInformation__keyInformation}
          title={t("companies_client.header.localisation")}
          icon={IconPin}
          colorReverse
          noSpacing
        >
          <div className={styles.headerKeyInformation__headquarter_address}>
            <Tooltip label={headquarterAddress} tooltipClassName={styles.headerKeyInformation__tooltip}>
              <Text
                variant="body1"
                weight="semiBold"
                data-testid="company_header_address"
                className={styles.headerKeyInformation__localization}
              >
                {headquarterAddress}
              </Text>
            </Tooltip>
            <a
              href={`http://maps.google.com/?q=${headquarterAddress}`}
              target="_blank"
              aria-label={`Google maps ${t("companies_client.link.target_blank")}`}
              rel="noopener noreferrer"
              className={styles.headerKeyInformation__localization_link}
              onClick={() => companyExternalLinkTrack(`http://maps.google.com/?q=${headquarterAddress}`, "googlemaps")}
            >
              <ExternalLink />
            </a>
          </div>
        </KeyInformation>
      )}
    </div>
  )
}
